import React from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, User } from "lucide-react";
import type { TelemetryNote } from "@/types";
import { formatTimeOffset } from "@/hooks/useTelemetryNotes";
import { formatUserName } from "@/utils/userName";

// Component for displaying notes in a side panel or list
interface TelemetryNotesListProps {
  notes: TelemetryNote[];
  onNoteClick: (note: TelemetryNote) => void;
  onTimeSeek?: (timeOffset: number) => void;
  selectedNoteId?: string | null;
 className?: string;
}

export const TelemetryNotesList: React.FC<TelemetryNotesListProps> = ({
  notes,
  onNoteClick,
  onTimeSeek,
  selectedNoteId,
  className = "",
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const sortedNotes = [...notes].sort((a, b) => a.timeOffset - b.timeOffset);

  if (notes.length === 0) {
    return (
 <div className={`text-center py-8 text-secondary-500 ${className}`}>
 <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{t("pages:sessions.telemetry.notes.noNotesYet")}</p>
        <p className="text-sm">
          {t("pages:sessions.telemetry.notes.clickChartToAdd")}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="telemetry-notes-list"
 className={`space-y-2 ${className}`}
    >
 <div className="flex items-center gap-2 text-sm font-medium text-secondary-700 mb-3">
 <MessageSquare className="h-4 w-4" />
        <span>
          {t("pages:sessions.telemetry.notes.count", {
            count: notes.length,
          })}
        </span>
      </div>

      {sortedNotes.map((note) => (
        <div
          key={note.id}
 className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
            selectedNoteId === note.id
              ? "border-purple-300 bg-purple-50 shadow-sm"
              : "border-secondary-200 bg-white hover:border-secondary-300 hover:shadow-sm"
          }`}
          onClick={() => onNoteClick(note)}
        >
 <div className="flex items-start justify-between gap-2 mb-2">
 <div className="flex items-center gap-2 text-sm text-secondary-600">
 <User className="h-3 w-3" />
 <span className="font-medium">
                {formatUserName(note.user)}
              </span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTimeSeek?.(note.timeOffset);
              }}
 className="text-xs bg-secondary-100 hover:bg-secondary-200 px-2 py-1 rounded transition-colors"
              title={t("pages:sessions.telemetry.notes.goToThisTime")}
            >
              {formatTimeOffset(note.timeOffset)}
            </button>
          </div>

 <p className="text-sm text-secondary-800 leading-relaxed">
            {note.content}
          </p>

          {note.createdAt && (
 <div className="text-xs text-secondary-500 mt-2">
              {new Date(note.createdAt).toLocaleString()}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
